import { ErrorHandler, Injectable, inject } from '@angular/core';
import type { Json } from '../../../shared/data/db.types';
import { SupabaseService } from '../../../shared/services/supabase.service';
import type { BrandSlug } from '../shared/brand-slug';
import type { EAN } from '../shared/ean';
import { fromMasked, toMasked } from '../shared/masked-ean';
import type {
  ProductScorecard,
  ScorecardField,
  ScorecardFieldStats,
  SnapshotWeek,
  VendorBrandScorecard,
  VendorProductContent,
} from './scorecard';
import { ScorecardRepository } from './scorecard-repository';

const TABLE = 'portal_scorecard';

// Every read pins the live snapshot: the table keeps superseded ones under other versions.
const CURRENT_SNAPSHOT = 'current';

// Rows, not weeks: `type='vendor_brand'` holds one row per vendor per week.
const WEEK_LIST_ROW_LIMIT = 80;

const SCORECARD_FIELDS: readonly ScorecardField[] = [
  'name',
  'desc',
  'brand',
  'size',
  'dosage',
  'contents',
];

@Injectable()
export class SupabaseScorecardRepository extends ScorecardRepository {
  // Not AuthStore: that import is portal-domain-no-portal-outside-domain.
  private readonly supabase = inject(SupabaseService);
  private readonly errorHandler = inject(ErrorHandler);

  async weeksOf(brand: BrandSlug): Promise<readonly SnapshotWeek[]> {
    const { data, error } = await this.supabase.client
      .from(TABLE)
      .select('snapshot_week')
      .eq('snapshot_ver', CURRENT_SNAPSHOT)
      .eq('type', 'vendor_brand')
      .eq('row_id', brand)
      .order('snapshot_week', { ascending: false })
      .limit(WEEK_LIST_ROW_LIMIT);
    if (error) throw error;

    const weeks = new Set<SnapshotWeek>();
    for (const row of (data ?? []) as { snapshot_week: string | null }[]) {
      if (row.snapshot_week) weeks.add(row.snapshot_week);
    }
    return [...weeks].sort().reverse();
  }

  async vendorScorecardsOf(
    brand: BrandSlug,
    week: SnapshotWeek | null
  ): Promise<readonly VendorBrandScorecard[]> {
    let query = this.supabase.client
      .from(TABLE)
      .select('row_vendor, snapshot_week, data')
      .eq('snapshot_ver', CURRENT_SNAPSHOT)
      .eq('type', 'vendor_brand')
      .eq('row_id', brand);
    if (week) query = query.eq('snapshot_week', week);

    const { data, error } = await query;
    if (error) throw error;

    const scorecards: VendorBrandScorecard[] = [];
    const defects: PayloadDefect[] = [];
    let unstoredKeys = 0;
    let unusableRows = 0;
    for (const row of (data ?? []) as VendorBrandRow[]) {
      const mapped = rowToVendorBrandScorecard(row);
      if (!mapped.scorecard) {
        unusableRows++;
        continue;
      }
      scorecards.push(mapped.scorecard);
      unstoredKeys += mapped.unstoredKeys;
      defects.push(...mapped.defects);
    }
    this.reportUnstoredKeys('vendorScorecardsOf', unstoredKeys);
    this.reportUnusableRows('vendorScorecardsOf', unusableRows);
    this.reportPayloadDefects('vendorScorecardsOf', defects);
    return scorecards;
  }

  async productScorecardOf(ean: EAN, week: SnapshotWeek | null): Promise<ProductScorecard | null> {
    let query = this.supabase.client
      .from(TABLE)
      .select('snapshot_week, data')
      .eq('snapshot_ver', CURRENT_SNAPSHOT)
      .eq('type', 'product')
      // The row is keyed by the stored form, so the real code is encoded on the way out.
      .eq('ean', toMasked(ean));
    if (week) query = query.eq('snapshot_week', week);

    // Ordered so `.limit(1)` on the `week === null` arm is the newest row, not an arbitrary one.
    const { data, error } = await query.order('snapshot_week', { ascending: false }).limit(1);
    if (error) throw error;

    const row = ((data ?? []) as ProductRow[])[0];
    if (!row) return null;

    const mapped = rowToProductScorecard(row, ean);
    this.reportUnusableRows('productScorecardOf', mapped.scorecard ? 0 : 1);
    this.reportPayloadDefects('productScorecardOf', mapped.defects);
    return mapped.scorecard;
  }

  // One report per read, never per row (.claude/rules/data-access.md, "never per item").
  private reportUnstoredKeys(method: string, count: number): void {
    if (count === 0) return;
    this.errorHandler.handleError(
      new Error(
        `[ScorecardRepository] ${method}: ${count} portal_scorecard key(s) are not a masked EAN; skipped`
      )
    );
  }

  // A row naming no vendor or no week has no heading to render under.
  private reportUnusableRows(method: string, count: number): void {
    if (count === 0) return;
    this.errorHandler.handleError(
      new Error(
        `[ScorecardRepository] ${method}: ${count} portal_scorecard row(s) name no vendor or no snapshot week; skipped`
      )
    );
  }

  private reportPayloadDefects(method: string, defects: readonly PayloadDefect[]): void {
    const counts = new Map<PayloadDefect, number>();
    for (const defect of defects) counts.set(defect, (counts.get(defect) ?? 0) + 1);
    for (const [defect, count] of counts) {
      this.errorHandler.handleError(
        new Error(
          `[ScorecardRepository] ${method}: ${count} portal_scorecard row(s) where ${DEFECT_TEXT[defect]}; read as empty`
        )
      );
    }
  }
}

// A payload container that arrived as something other than the shape it holds. Counted apart
// from unstored keys: one shape break is one event, not one per key it would have held.
type PayloadDefect = 'data' | 'eans' | 'metrics' | 'vendors';

const DEFECT_TEXT: Readonly<Record<PayloadDefect, string>> = {
  data: 'data is not an object',
  eans: 'data.eans is not an array',
  metrics: 'data.metrics is not an object',
  vendors: 'data.vendors is not an object',
};

// An object inside the payload, as JSONB permits it: every member still unnarrowed.
type JsonRecord = { [key: string]: Json | undefined };

// `data` is JSONB with no shape constraint, and `NOT NULL` columns are narrowed anyway
// (.claude/rules/data-access.md, "Reading rows: validate once").
type VendorBrandRow = {
  readonly row_vendor?: unknown;
  readonly snapshot_week?: unknown;
  readonly data: Json;
};

type ProductRow = {
  readonly snapshot_week?: unknown;
  readonly data: Json;
};

function rowToVendorBrandScorecard(row: VendorBrandRow): {
  scorecard: VendorBrandScorecard | null;
  unstoredKeys: number;
  defects: readonly PayloadDefect[];
} {
  const defects: PayloadDefect[] = [];

  const vendor = asText(row.row_vendor);
  const week = asText(row.snapshot_week);
  if (!vendor || !week) return { scorecard: null, unstoredKeys: 0, defects };

  const payload = asRecord(row.data, 'data', defects);
  const metrics = asRecord(payload['metrics'], 'metrics', defects);

  const eans: EAN[] = [];
  let unstoredKeys = 0;
  const listed = payload['eans'];
  if (Array.isArray(listed)) {
    for (const key of listed) {
      const ean = typeof key === 'string' ? fromMasked(key) : null;
      if (ean) eans.push(ean);
      else unstoredKeys++;
    }
  } else if (listed != null) {
    // A string would iterate as characters and a number would throw; report the shape instead.
    defects.push('eans');
  }

  return {
    scorecard: {
      vendor,
      week,
      productCount: asNumber(payload['product_count']) ?? 0,
      eans,
      stats: toFieldStats(metrics),
      avgImageCount: asNumber(statsAt(metrics, 'img_count')?.['avg_count']),
    },
    unstoredKeys,
    defects,
  };
}

function rowToProductScorecard(
  row: ProductRow,
  ean: EAN
): { scorecard: ProductScorecard | null; defects: readonly PayloadDefect[] } {
  const defects: PayloadDefect[] = [];

  const week = asText(row.snapshot_week);
  if (!week) return { scorecard: null, defects };

  const payload = asRecord(row.data, 'data', defects);
  const metrics = asRecord(payload['metrics'], 'metrics', defects);

  const vendors = new Map<string, VendorProductContent>();
  for (const [vendor, content] of Object.entries(
    asRecord(payload['vendors'], 'vendors', defects)
  )) {
    if (!isRecord(content)) continue;
    vendors.set(vendor, toVendorProductContent(content));
  }

  const avgFieldLengths: Partial<Record<ScorecardField, number>> = {};
  for (const field of SCORECARD_FIELDS) {
    const avg = asNumber(statsAt(metrics, field)?.['avg_length_this_product']);
    if (avg !== null) avgFieldLengths[field] = avg;
  }

  return { scorecard: { ean, week, vendors, avgFieldLengths }, defects };
}

function toFieldStats(
  metrics: JsonRecord
): Readonly<Partial<Record<ScorecardField, ScorecardFieldStats>>> {
  const stats: Partial<Record<ScorecardField, ScorecardFieldStats>> = {};
  for (const field of SCORECARD_FIELDS) {
    const measured = statsAt(metrics, field);
    if (!measured) continue;
    stats[field] = {
      fillPct: asNumber(measured['fill_pct']),
      avgLength: asNumber(measured['avg_length']),
      filledValues: asNumber(measured['filled_values']),
    };
  }
  return stats;
}

function toVendorProductContent(content: JsonRecord): VendorProductContent {
  const fields: Record<ScorecardField, string | null> = {
    name: asText(content['name']),
    desc: asText(content['desc']),
    brand: asText(content['brand']),
    size: asText(content['size']),
    dosage: asText(content['dosage']),
    contents: asText(content['contents']),
  };
  return { ...fields, url: asText(content['url']), imgCount: asNumber(content['img_count']) ?? 0 };
}

// A container of the wrong shape is read as empty: walking it would invent members
// (.claude/rules/data-access.md, "Reading rows: validate once").
function asRecord(
  value: Json | undefined,
  defect: PayloadDefect,
  defects: PayloadDefect[]
): JsonRecord {
  if (value == null) return {};
  if (!isRecord(value)) {
    defects.push(defect);
    return {};
  }
  return value;
}

function isRecord(value: Json | undefined): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// One nested statistics object, or null when the member is absent or is not an object.
function statsAt(metrics: JsonRecord, key: string): JsonRecord | null {
  const value = metrics[key];
  return isRecord(value) ? value : null;
}

// Leaves carry no shape constraint (.claude/rules/data-access.md, "Reading rows: validate once").
function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
